import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, inject, computed, signal,} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { SidebarService } from './sidebar.service';
import {
  IconComponent,
  UserStateService,
  UserRole,
  SchemaMembershipStateService,
  SchemaUiService,
} from '@net3-multiplatform-client/libs';

type NavItem = {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  roles?: UserRole[];
  visible?: boolean;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, IconComponent],
  templateUrl: './sidebar.component.html',
})
export class AppSidebarComponent {

  private schemaUIService = inject(SchemaUiService);
  
  navItems = computed(() => {
    const user = this.userStateService.user();
    if (!user) {
      return [];
    }

    // obté membership del SchemaMembershipStateService
    const membership = this.schemaMembershipState.schema();
    // si no hi ha membresia a l'schema, o no hi ha rol, no mostrem cap menu de navegació.
    // la comprovació es redundant, ja que si no tens membresia els guards no et deixen accedir a les rutes d'una comunitat.
    // No obstant, no està de mes
    if (!membership || !membership.role) return [];

    const items: NavItem[] = [
      {
        name: 'Inicio',
        icon: 'home',
        path: './home',
      },
      {
        name: 'Tomas',
        icon: 'irrigation-outlet',
        path: './irrigation-outlets',
      },
      {
        name: 'Sectores',
        icon: 'sector',
        path: './sectors',
      },
      {
        name: 'Previsión de riego',
        icon: 'cart',
        path: './irrigation-schedule',
        visible: this.isIrrigationScheduleEnabled(),
      },
      {
        name: 'Sensores',
        icon: 'sensor',
        path: './sensors',
      },
      {
        name: 'Historial',
        icon: 'calendar',
        path: './history',
      },
      {
        name: 'Usuarios',
        icon: 'nav-users',
        path: './users',
        roles: [UserRole.GESTOR, UserRole.ADMIN], // Només visible per gestors i admins
      },
    ];

    return items.filter((item) => {
      if (item.visible === false) {
        return false;
      }
      if (!item.roles || item.roles.length === 0) {
        return true;
      }
      return item.roles.some((permittedRole) => permittedRole.includes(membership.role));
    });
  });

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private userStateService = inject(UserStateService);
  private schemaMembershipState = inject(SchemaMembershipStateService);

  isIrrigationScheduleEnabled = computed(() => {
    const props = this.schemaMembershipState.properties();
    return props?.irrigationSchedule ?? false;
  });

  membershipName = computed(() => {
    return this.schemaMembershipState.schema()?.name || '';
  });

  getShortSchemaName(schemaName: string) {
    return this.schemaUIService.getShortSchemaName(schemaName);
  }

  private subscription: Subscription = new Subscription();
  routerLinkActive: any;

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    // Subscribe to router events
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    // Subscribe to combined observables to close submenus when all are false
    this.subscription.add(
      combineLatest([
        this.isExpanded$,
        this.isMobileOpen$,
        this.isHovered$,
      ]).subscribe(([isExpanded, isMobileOpen, isHovered]) => {
        if (!isExpanded && !isMobileOpen && !isHovered) {
          // this.openSubmenu = null;
          // this.savedSubMenuHe ights = { ...this.subMenuHeights };
          // this.subMenuHeights = {};
          this.cdr.detectChanges();
        } else {
          // Restore saved heights when reopening
          // this.subMenuHeights = { ...this.savedSubMenuHeights };
          // this.cdr.detectChanges();
        }
      })
    );

    // Initial load
    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges(); // Ensure UI updates
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$
      .subscribe((expanded) => {
        if (!expanded) {
          this.sidebarService.setHovered(true);
        }
      })
      .unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    const menuGroups = [{ items: this.navItems(), prefix: 'main' }];

    menuGroups.forEach((group) => {
      group.items.forEach((nav: NavItem, i: number) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (currentUrl === subItem.path) {
              const key = `${group.prefix}-${i}`;
              this.openSubmenu = key;

              setTimeout(() => {
                const el = document.getElementById(key);
                if (el) {
                  this.subMenuHeights[key] = el.scrollHeight;
                  this.cdr.detectChanges(); // Ensure UI updates
                }
              });
            }
          });
        }
      });
    });
  }

  onSubmenuClick() {
    this.isMobileOpen$
      .subscribe((isMobile) => {
        if (isMobile) {
          this.sidebarService.setMobileOpen(false);
        }
      })
      .unsubscribe();
  }

  onSingleMenuClick() {
    // Close any open submenu
    this.openSubmenu = null;
    this.subMenuHeights = {};

    // Also close sidebar if mobile
    this.isMobileOpen$
      .subscribe((isMobile) => {
        if (isMobile) {
          this.sidebarService.setMobileOpen(false);
        }
      })
      .unsubscribe();
  }
  
}
